import React from 'react';

import SimpleRoundedButton from '#/components/basic/Button/SimpleRoundedButton';
import RouterLink from '#/components/basic/Link/RouterLink';
import { KATANA_PERPS } from '#/config/constants';
import { formatTime } from '#/lib/date';
import { formatNumber } from '#/lib/display/formatting';
import { capitalizeFirstLetter } from '#/lib/string';
import { UnreachableCaseError } from '#/lib/utils';
import { staticDispatch } from '#/reduxState/store';
import { showWalletModal } from '#/reduxState/wallet/walletDetailsReducer';

import { closeDisplayedNotification } from './notificationsUtils';
import { NotificationObject } from './notificationTypes';

function Time({ time }: { time: number }) {
  return <b>{formatTime(time)}&nbsp; </b>;
}

export function renderNotification(notif: NotificationObject) {
  if (!notif) {
    return;
  }

  const { type } = notif;
  switch (type) {
    case 'depositCredited':
      return messageDepositCredited({
        quantity: notif.quantity,
        time: notif.time,
      });
    case 'orderPlaced':
      return (
        <div>
          <Time time={notif.time} /> Placed {notif.side}{' '}
          {formatNumber(notif.originalQuantity, {
            fixedDecimals: notif.quantityDecimals,
          })}{' '}
          {notif.baseSymbol}{' '}
          {notif.price && (
            <>
              at $
              {formatNumber(notif.price, {
                fixedDecimals: notif.priceDecimals,
              })}
            </>
          )}
          . {notif.trigger && <>Trigger: {notif.trigger}.</>}&nbsp;
          <RouterLink to="/wallet/open-orders" color="white" underline>
            Open Orders
          </RouterLink>
        </div>
      );
    case 'orderTPSLPlaced':
      return (
        <div>
          <Time time={notif.time} /> Placed {notif.side} to close{' '}
          {notif.baseSymbol}. Trigger: {notif.trigger}.<br />
          <RouterLink to="/wallet/open-orders" color="white" underline>
            Open Orders
          </RouterLink>
        </div>
      );
    case 'orderFilled':
      return (
        <div>
          <Time time={notif.time} /> {capitalizeFirstLetter(notif.side)}{' '}
          {formatNumber(notif.quantity, {
            fixedDecimals: notif.quantityDecimals,
          })}{' '}
          {notif.baseSymbol} at ~$
          {formatNumber(notif.avgExecutionPrice, {
            fixedDecimals: notif.priceDecimals,
          })}
          .
        </div>
      );
    case 'orderPartiallyFilled':
      return (
        <div>
          <Time time={notif.time} /> {capitalizeFirstLetter(notif.side)}{' '}
          {formatNumber(notif.executedQuantity, {
            fixedDecimals: notif.quantityDecimals,
          })}{' '}
          of{' '}
          {formatNumber(notif.originalQuantity, {
            fixedDecimals: notif.quantityDecimals,
          })}{' '}
          {notif.baseSymbol} at ~$
          {formatNumber(notif.avgExecutionPrice, {
            fixedDecimals: notif.priceDecimals,
          })}
          .
        </div>
      );
    case 'orderTPSLCanceled':
      return (
        <div>
          <Time time={notif.time} /> {notif.baseSymbol}{' '}
          {/* Complete type-safe check */}
          {notif.orderType === 'takeProfitLimit' ||
          notif.orderType === 'takeProfitMarket' ||
          notif.orderType.includes('takeProfit')
            ? 'Take Profit'
            : notif.orderType === 'stopLossLimit' ||
              notif.orderType === 'stopLossMarket' ||
              notif.orderType.includes('stopLoss')
            ? 'Stop Loss'
            : 'Unknown'}{' '}
          canceled.
        </div>
      );
    case 'orderCanceled':
      return (
        <div>
          <Time time={notif.time} /> {capitalizeFirstLetter(notif.side)}{' '}
          {formatNumber(notif.originalQuantity, {
            fixedDecimals: notif.quantityDecimals,
          })}{' '}
          {notif.baseSymbol}{' '}
          {notif.price ? (
            <>
              at $
              {formatNumber(notif.price, {
                fixedDecimals: notif.priceDecimals,
              })}{' '}
            </>
          ) : // No price available for not-executed market order (not enough liquidity in market)
          null}
          canceled.
          {/* {!notif.price &&
            // TODO: text
            ' This might have been caused by not enough liquidity in market.'} */}
        </div>
      );
    case 'orderError':
      return (
        <div>
          <Time time={notif.time} /> {notif.baseSymbol} Order rejected with
          error: {notif.message}.
        </div>
      );
    case 'orderDeleverage':
      return (
        <div>
          <Time time={notif.time} /> {notif.baseSymbol}{' '}
          {notif.side === 'buy' ? 'Short' : 'Long'} closed due to ADL.&nbsp;
          <RouterLink to="/wallet/history/trades" color="white" underline>
            Trade History
          </RouterLink>
        </div>
      );
    case 'walletLiquidation':
      // (TIME) 123 BTC Long has been liquidated. link: Trade History
      return (
        <div>
          <Time time={notif.time} />
          {notif.baseSymbol} {notif.side === 'buy' ? 'Short' : 'Long'} has been
          liquidated.&nbsp;
          <RouterLink to="/wallet/history/trades" color="white" underline>
            Trade History
          </RouterLink>
        </div>
      );
    case 'dustPositionClose':
      return (
        <div>
          <Time time={notif.time} />
          {notif.baseSymbol} {notif.side === 'buy' ? 'Short' : 'Long'} below
          minimum has been closed.
        </div>
      );
    case 'delegatedKeyInvalid':
    case 'delegatedKeyExpiringNotice':
    case 'delegatedKeyExpired':
      return (
        <div>
          <Time time={notif.time} />
          {type === 'delegatedKeyExpiringNotice' ? (
            <>
              {KATANA_PERPS} session will expire in {notif.remainingTime},
              please re-authorize.
            </>
          ) : type === 'delegatedKeyInvalid' ? (
            <>{KATANA_PERPS} session is no longer valid, please re-authorize.</>
          ) : (
            <>{KATANA_PERPS} session expired. Connect your wallet again.</>
          )}
          <div style={{ height: 8 }} />
          <SimpleRoundedButton
            type="yellow2"
            onClick={() => {
              staticDispatch(
                showWalletModal({
                  keepWalletUntilSuccess: true,
                  preSelect: notif.walletType
                    ? {
                        // TODO: unlock calling signature twice !
                        remember: notif.walletRemember,
                        walletType: notif.walletType,
                        rdns: notif.walletRdns,
                      }
                    : undefined,
                })
              );
              closeDisplayedNotification(type);
            }}
          >
            Re-Authorize
          </SimpleRoundedButton>
        </div>
      );
    case 'payoutSuccess':
      return (
        <div>
          <Time time={notif.time} />
          Reward claim complete.
        </div>
      );
    default:
      throw new UnreachableCaseError(type);
  }
}

// Here are extra generated messages which might be replaced by websocket in future

export function messageDepositCredited({
  time,
  quantity,
}: {
  time: number;
  quantity: string;
}) {
  return (
    <div>
      <Time time={time} /> Deposit of{' '}
      {formatNumber(quantity, { fixedDecimals: 2 })} USDC completed.&nbsp;
      <RouterLink
        to="/wallet/history/deposits-withdrawals"
        color="white"
        underline
      >
        Deposits
      </RouterLink>
    </div>
  );
}

export function messageDepositFailed() {
  return (
    <div>
      <Time time={Date.now()} /> Your USDC deposit failed.&nbsp;
      <RouterLink to="/wallet/history/deposits-withdrawals">
        Deposits
      </RouterLink>
    </div>
  );
}

export function warningOrErrorMessage(message: string) {
  return (
    <div>
      <Time time={Date.now()} /> {message}
    </div>
  );
}

export const throttledNotificationsMsg =
  'Paused notifications for a moment. They will automatically resume when the current volume decreases.';
